import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  FileText,
  FolderOpen,
  Loader2,
  ScrollText,
  type LucideIcon,
} from "lucide-react";
import {
  lookbookApi,
  type LookbookShareOptions,
  type LookbookVisibilityInput,
} from "@/api/lookbook-api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { lookbookReportMeta } from "@/lib/lookbook-reports";
import { cn } from "@/lib/utils";
import {
  availableDocumentTypes,
  availableReports,
  buildVisibilityInput,
  draftFromCurrent,
  emptyDraft,
  groupDocumentsByType,
  type VisibilityDraft,
} from "./lookbook-visibility-utils";

export interface LookbookVisibilityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "existing" | "create";
  lookbookId?: string;
  lookbookName: string;
  companyIds: string[];
  workspaceId: string;
  isSaving: boolean;
  onConfirm: (payload: {
    name: string;
    visibility: LookbookVisibilityInput;
  }) => void;
}

const EMPTY_OPTIONS: LookbookShareOptions = {
  companies: [],
  document_types: [],
  reports: [],
};

/**
 * "What should this lookbook show?" — chosen when a deal is filed, applied to
 * that deal in that lookbook only.
 *
 * The bulk case is the one that shapes the UI. Across N deals the documents are
 * different files, so the picker offers *categories* ("Pitch Deck", "Cap
 * Table") and the server resolves each deal's own newest file of that type. A
 * deal that has no file of a ticked category simply contributes nothing. For a
 * single deal there is no ambiguity, so it lists the actual files.
 *
 * Nothing is offered that doesn't exist: a category no deal has, or a report
 * nobody has generated, is absent rather than disabled. A tick that resolves to
 * an empty section on a public page is worse than one option fewer.
 */
export function LookbookVisibilityDialog({
  open,
  onOpenChange,
  mode,
  lookbookId,
  lookbookName,
  companyIds,
  workspaceId,
  isSaving,
  onConfirm,
}: LookbookVisibilityDialogProps) {
  const [draft, setDraft] = useState<VisibilityDraft>(emptyDraft);
  const [name, setName] = useState(lookbookName);
  const seededFromServer = useRef(false);

  const isBulk = companyIds.length > 1;
  const sortedIds = useMemo(() => [...companyIds].sort(), [companyIds]);

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "lookbook-share-options",
      workspaceId,
      lookbookId ?? "new",
      sortedIds.join(","),
    ],
    queryFn: () =>
      lookbookApi.getShareOptions(workspaceId, companyIds, lookbookId),
    enabled: open && companyIds.length > 0 && !!workspaceId,
    staleTime: 30_000,
  });

  const options = data ?? EMPTY_OPTIONS;

  // Reset when the dialog opens, so the previous add's choices don't bleed
  // into the next one.
  useEffect(() => {
    if (!open) return;
    setName(lookbookName);
    setDraft(emptyDraft());
    seededFromServer.current = false;
  }, [open, lookbookName]);

  // Then, once, adopt whatever config this deal already has. Guarded by a ref
  // rather than run on every `data` change: the query refetches, and re-seeding
  // would silently throw away ticks the user made in the meantime.
  useEffect(() => {
    if (!open || !data || seededFromServer.current) return;
    seededFromServer.current = true;
    if (data.companies.length === 1 && data.companies[0].current) {
      setDraft(draftFromCurrent(data));
    }
  }, [open, data]);

  const documentTypes = useMemo(
    () => availableDocumentTypes(options),
    [options]
  );
  const reports = useMemo(() => availableReports(options), [options]);
  const singleCompanyGroups = useMemo(
    () =>
      !isBulk && options.companies[0]
        ? groupDocumentsByType(options.companies[0].documents)
        : [],
    [isBulk, options]
  );

  const hasDocuments = isBulk
    ? documentTypes.length > 0
    : singleCompanyGroups.length > 0;
  const hasReports = reports.length > 0;

  const toggle = (key: keyof VisibilityDraft, value: boolean) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const toggleIn = (key: "documentTypes" | "documentIds" | "reportKeys") =>
    (value: string, checked: boolean) =>
      setDraft((d) => ({
        ...d,
        [key]: checked
          ? [...d[key], value]
          : d[key].filter((v) => v !== value),
      }));

  const toggleType = toggleIn("documentTypes");
  const toggleFile = toggleIn("documentIds");
  const toggleReport = toggleIn("reportKeys");

  const dealLabel = `${companyIds.length} deal${
    companyIds.length === 1 ? "" : "s"
  }`;

  // No "pick at least one thing" gate: the profile is always on, so the
  // selection can never resolve to an empty page.
  const confirmDisabled =
    isSaving || (mode === "create" && name.trim().length === 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 p-0">
        <DialogHeader className="space-y-1.5 border-b p-6">
          <DialogTitle>What should this lookbook show?</DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? `Choose what ${dealLabel} will show to anyone with the link.`
              : `${lookbookName} · ${dealLabel}`}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto p-6">
          {mode === "create" && (
            <div className="space-y-1.5 pb-2">
              <Label htmlFor="lookbook-name">Lookbook name</Label>
              <Input
                id="lookbook-name"
                value={name}
                maxLength={80}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          )}

          {isError && (
            <p className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
              Couldn't load what's available to share. You can still add{" "}
              {companyIds.length === 1 ? "this deal" : "these deals"} showing
              the profile.
            </p>
          )}

          {/* Locked on. The profile is what a lookbook page *is* — without it a
              visitor lands on a bare file list under a company name — so it is
              shown as a fixed part of the page, not a choice. */}
          <VisibilitySection
            icon={FileText}
            title="Profile"
            subtitle="Overview, round details, problem, solution and traction."
            checked
            locked
            lockedNote="Always shown"
            onCheckedChange={() => {}}
          />

          <VisibilitySection
            icon={FolderOpen}
            title="Data room"
            subtitle={
              hasDocuments
                ? "Share specific documents from the data room."
                : "No documents have been uploaded yet."
            }
            checked={draft.dataRoom}
            disabled={!hasDocuments}
            onCheckedChange={(v) => toggle("dataRoom", v)}
          >
            {isLoading ? (
              <OptionSkeleton />
            ) : isBulk ? (
              documentTypes.map((option) => (
                <OptionRow
                  key={option.value}
                  id={`doc-type-${option.value}`}
                  label={option.label}
                  meta={countLabel(option.companyCount, companyIds.length)}
                  checked={draft.documentTypes.includes(option.value)}
                  onCheckedChange={(c) => toggleType(option.value, c)}
                />
              ))
            ) : (
              singleCompanyGroups.map((group) => (
                <div key={group.fileType} className="space-y-1">
                  <p className="pt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </p>
                  {group.files.map((file) => (
                    <OptionRow
                      key={file._id}
                      id={`doc-${file._id}`}
                      label={file.file_name}
                      meta={formatFileMeta(file.file_size, file.time_stamp)}
                      checked={draft.documentIds.includes(file._id)}
                      onCheckedChange={(c) => toggleFile(file._id, c)}
                    />
                  ))}
                </div>
              ))
            )}
          </VisibilitySection>

          <VisibilitySection
            icon={ScrollText}
            title="Reports"
            subtitle={
              hasReports
                ? "Share generated reports and analysis."
                : "No reports have been generated yet."
            }
            checked={draft.reports}
            disabled={!hasReports}
            onCheckedChange={(v) => toggle("reports", v)}
          >
            {isLoading ? (
              <OptionSkeleton />
            ) : (
              reports.map((option) => {
                const meta = lookbookReportMeta(option.value);
                const Icon = meta.icon;
                return (
                  <OptionRow
                    key={option.value}
                    id={`report-${option.value}`}
                    label={option.label}
                    icon={Icon}
                    meta={
                      isBulk
                        ? countLabel(option.companyCount, companyIds.length)
                        : meta.description
                    }
                    checked={draft.reportKeys.includes(option.value)}
                    onCheckedChange={(c) => toggleReport(option.value, c)}
                  />
                );
              })
            )}
          </VisibilitySection>
        </div>

        <DialogFooter className="border-t p-4">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            disabled={confirmDisabled}
            onClick={() =>
              onConfirm({
                name: name.trim(),
                visibility: buildVisibilityInput(
                  draft,
                  options,
                  companyIds.length
                ),
              })
            }
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === "create"
              ? "Create lookbook"
              : `Add ${dealLabel} to ${lookbookName}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One toggleable section of the public page.
 *
 * `disabled` and `locked` both freeze the switch but mean opposite things, and
 * the styling has to keep them apart: `disabled` is "there is nothing here to
 * share" and reads as unavailable (dimmed, switch off); `locked` is "this is
 * always shared" and must read as fully present (normal weight, switch on, a
 * short note saying why it can't be changed).
 */
function VisibilitySection({
  icon: Icon,
  title,
  subtitle,
  checked,
  disabled,
  locked,
  lockedNote,
  onCheckedChange,
  children,
}: {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  checked: boolean;
  disabled?: boolean;
  locked?: boolean;
  lockedNote?: string;
  onCheckedChange: (checked: boolean) => void;
  children?: React.ReactNode;
}) {
  const on = locked ? true : checked && !disabled;

  return (
    <div
      className={cn(
        "rounded-lg border border-border",
        disabled && !locked && "opacity-60"
      )}
    >
      <div className="flex items-start gap-3 p-4">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        {locked && lockedNote && (
          <span className="mt-0.5 shrink-0 text-xs text-muted-foreground">
            {lockedNote}
          </span>
        )}
        <Switch
          checked={on}
          disabled={disabled || locked}
          onCheckedChange={onCheckedChange}
          aria-label={title}
          aria-readonly={locked || undefined}
          // A disabled switch is inert, so the note above is the only thing
          // telling a screen reader why. Keep it in the accessible name.
          title={locked ? lockedNote : undefined}
        />
      </div>
      {on && children && (
        <div className="space-y-1 border-t border-border px-4 py-3">
          {children}
        </div>
      )}
    </div>
  );
}

function OptionRow({
  id,
  label,
  meta,
  icon: Icon,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  meta?: string;
  icon?: LucideIcon;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-center gap-2.5 rounded-md px-1 py-1.5 hover:bg-muted/50"
    >
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(c) => onCheckedChange(c === true)}
      />
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      <span className="min-w-0 flex-1 truncate text-sm">{label}</span>
      {meta && (
        <span className="shrink-0 text-xs text-muted-foreground">{meta}</span>
      )}
    </label>
  );
}

function OptionSkeleton() {
  return (
    <div className="space-y-2 py-1">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-6 w-full" />
      ))}
    </div>
  );
}

const countLabel = (available: number, total: number) =>
  available >= total ? `all ${total}` : `${available} of ${total}`;

function formatFileMeta(size?: string, timestamp?: string): string {
  const parts: string[] = [];
  if (size) parts.push(size);
  if (timestamp) {
    const date = new Date(timestamp);
    if (!Number.isNaN(date.getTime())) {
      parts.push(
        date.toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      );
    }
  }
  return parts.join(" · ");
}
